import React from "react";
import {
    DataGrid,
} from "@mui/x-data-grid";
import {
    Box,
    Button,
    Chip,
    Typography,
    Modal,
    FormControl,
    Autocomplete,
    TextField
} from "@mui/material";
import {
    CheckCircle,
} from 'react-feather';
import { useDispatch, useSelector } from "react-redux";
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import Page from '../../components/Page';
import { sortByString } from '../../utils/sortByString';
import SentBotmarkEstimateViewer from './SentBotmarkEstimateViewer';

import "./estimateTables.css";
import { estimateStatusValueGetter, invoiceTotalValueGetter } from '../../utils/columnValueGetters';
import { EMPTY_GUID } from '../../query/queryConfig';
import { useBotmarkInvoices } from '../../hooks/useInvoices';
import { useCompaniesList } from '../../hooks/useCompanies';
import { downloadBotmarkInvoiceCsv } from '../../utils/buildBotmarkInvoiceExport';

function BotmarkEstimateTable(){
    /// view all invoices sent by company to customers ///
    const customerSelect = useSelector((state) => state.UserReducer.customerSelection);

    const [selectedComp, setSelectedComp] = React.useState(EMPTY_GUID);
    const [selectedCust, setSelectedCust] = React.useState(EMPTY_GUID);
    function setSelectedCompany(event){
        setSelectedComp(event)
    };
    function setSelectedCustomer(event){
        setSelectedCust(event)
    };

    const [startFilterListDate, setStartFilterListDate] = React.useState();
    const [endFilterListDate, setEndFilterListDate] = React.useState(null);
    const [listFilterDates, setListFilterDates] = React.useState(null);

    const { data: allCompanies = [] } = useCompaniesList({
        startDate: listFilterDates?.startDate,
        endDate: listFilterDates?.endDate,
    });

    let allCompaniesArray = sortByString(allCompanies ?? [], 'name');

    const defaultProps = {
        options: allCompaniesArray,
        getOptionLabel: (option) => option.name,
    };

    const startDateInitialState = dayjs().subtract(1, "years").startOf('day');
    const endDateInitialState = dayjs().endOf('day');
    const [startDate, setStartDate] = React.useState(null);
    const [endDate, setEndDate] = React.useState(null);
    const [exportStartDate, setExportStartDate] = React.useState(dayjs(startDateInitialState).format());
    const [exportEndDate, setExportEndDate] = React.useState(dayjs(endDateInitialState).format());

    function filterCompany(){
        setListFilterDates({ startDate: endFilterListDate, endDate: startFilterListDate });
    }

    function resetCompany(){
        setStartFilterListDate(null);
        setEndFilterListDate(null);
        setListFilterDates(null);
    }

    const [paginationModel, setPaginationModel] = React.useState({page: 0, pageSize: 25});

    const botmarkPollParams = React.useMemo(() => {
        const p = paginationModel;
        if (!selectedComp && !selectedCust || selectedComp == undefined && selectedCust == undefined) {
            return {
                companyId: EMPTY_GUID,
                customerId: EMPTY_GUID,
                page: p.page,
                pageSize: p.pageSize,
            };
        }
        if (!selectedComp || selectedComp == undefined) {
            return {
                companyId: EMPTY_GUID,
                customerId: selectedCust,
                page: p.page,
                pageSize: p.pageSize,
            };
        }
        if (!selectedCust || selectedCust == undefined) {
            return {
                companyId: selectedComp,
                customerId: EMPTY_GUID,
                page: p.page,
                pageSize: p.pageSize,
            };
        }
        return {
            companyId: selectedComp,
            customerId: selectedCust,
            page: p.page,
            pageSize: p.pageSize,
        };
    }, [selectedComp, selectedCust, paginationModel]);

    const { data: invoiceData, isFetching: isLoading } = useBotmarkInvoices({
        ...botmarkPollParams,
        startDate: exportStartDate,
        endDate: exportEndDate,
        enabled: Boolean(exportStartDate && exportEndDate),
    });

    const CompanyInvoices = invoiceData?.invoicesDTO ?? [];
    const ExportCompanyInvoices = invoiceData?.exportInvoicesDTO ?? [];
    const foundInvoiceCount = invoiceData?.foundTotalInvoiceCount ?? 0;
    const invoiceCount = invoiceData?.totalInvoiceCount ?? 0;
    const rowCountRef = React.useRef(foundInvoiceCount || 0);

    const rowCount = React.useMemo(() => {
        if (foundInvoiceCount !== undefined) {
            rowCountRef.current = foundInvoiceCount;
        }
        return rowCountRef.current;
    }, [foundInvoiceCount]);

    function applyInvoiceDateFilter() {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
    };

    function resetDate(){
        setSelectedComp();
        setSelectedCompany();
        setSelectedCust();
        setSelectedCustomer();
        setStartDate(null);
        setEndDate(null);
        setExportStartDate(dayjs(startDateInitialState).format());
        setExportEndDate(dayjs(endDateInitialState).format());
    };

    const [invoiceModelObj, setInvoiceModelObj] = React.useState({});
    const [invoiceModal, setInvoiceModal] = React.useState(false);
    const handleInvoiceOpen = () => setInvoiceModal(true);
    const handleInvoiceClose = () => setInvoiceModal(false);
    function showInvoice(x){
        setInvoiceModelObj({
            created: x?.created,
            customerEmail: x?.customerEmail,
            customerId: x?.customerId,
            customerName: x?.customerName,
            developerId: x?.developerId,
            developerName: x?.developerName,
            estimateId: x?.estimateId,
            fileCompanyId: x?.fileCompanyId,
            fileId: x?.fileId,
            fileIsAccepted: x?.fileIsAccepted,
            fileName: x?.fileName,
            id: x?.id,
            invoiceValues: x?.invoiceValues,
            isInvoiceAccepted: x?.isInvoiceAccepted,
            isPaidFor: x?.isPaidFor,
            isProcessed: x?.isProcessed,
            isSent: x?.isSent,
            jobId: x?.jobId,
            openInvoice: x?.openInvoice,
            pointCount: x?.pointCount,
            reportUrl: x?.reportUrl,
            sequencingErrorCode: x?.sequencingErrorCode,
            summaryUrl: x?.summaryUrl,
            updated: x?.updated,
            pointList: x?.pointList,
        })
        handleInvoiceOpen();
    };

    /// For Creating and Downloading Invoice as CSV //
    function exportToCsv() {
        const exportStartDateFormat = dayjs(exportStartDate).format('MM/DD/YYYY');
        const exportEndDateFormat = dayjs(exportEndDate).format('MM/DD/YYYY');
        const fileNamePrefix = customerSelect.userCompanyId != EMPTY_GUID
            ? customerSelect.userCompanyId
            : 'allCustomerEstimates';
        const fileName = `${fileNamePrefix}-${exportStartDateFormat}-${exportEndDateFormat}.csv`;
        downloadBotmarkInvoiceCsv(ExportCompanyInvoices, fileName, {
            startDateLabel: exportStartDateFormat,
            endDateLabel: exportEndDateFormat,
        });
    };

    const columns = [
        { field: 'developerName', headerName: 'Processor', width: 200, headerClassName: 'header', editable: false },
        { field: 'customerName', headerName: 'Customer', width: 200, headerClassName: 'header', editable: false },
        { field: 'fileName', headerName: 'Project Name', width: 250, headerClassName: 'header', editable: false},
        { field: 'invoice', headerName: 'Estimate', width: 200, headerClassName: 'header', sortable: false, renderCell: (params) => (
            Invoice(params.row)
        )},
        { field: 'total', headerName: 'Total', width: 100, headerClassName: 'header', valueGetter: invoiceTotalValueGetter,
            renderCell: (params) => {
                var data = JSON?.parse(params.row?.invoiceValues);
                return (<div>$ {data?.finalTotal}</div>)
        }},
        { field: 'updated', headerName: 'Updated', width: 200, headerClassName: 'header', renderCell: (params) => {
                let date = new Date(params.row.updated);
                return date.toLocaleString()
            }
        },
        { field: 'status', headerName: 'Status', width: 175, headerClassName: 'header', valueGetter: estimateStatusValueGetter, renderCell: (params) => {return (Status(params.row))}},
    ];
    function Status(x) {
        if(x.isProcessed == 1 || x.isProcessed == 4){
            x.status = "Complete";
            return <Chip icon={<CheckCircle/>} variant="contained" color="success" label="COMPLETE"/>
        } else if(x.isProcessed == 2){
            x.status = "Complete N/P";
            return <Chip icon={<CheckCircle/>} variant="contained" color="warning" label="COMPLETE N/P"/>
        }
    };

    function Invoice(x) {
        return (
            // download invoice
            <div>
                <Button variant="contained" color="success" onClick={() => showInvoice(x)}>
                    View/ Download
                </Button>
            </div>
        )
    };

    const rows = CompanyInvoices;

    return (
        <Page title="Botmark Invoices">
        <Box style={{paddingLeft:"5px", paddingRight:'20px'}}>
            <Box sx={{ marginTop: "10px", marginBottom: "10px" }}>
                <Typography textAlign={"center"} color={"blue"} variant='h4'>
                    Botmark Invoices
                </Typography>
                <Typography textAlign={"center"}>
                    Total Invoices: {invoiceCount}
                </Typography>
            </Box>
            <Box className="selectorDiv" style={{display:'flex', flexDirection:'row', justifyContent:'space-between'}}>
                <div className="selectorSubDiv" style={{width:'50%', display:'flex' ,border:'solid darkgrey 2px', padding:'2px'}}>
                <FormControl className="selectorChild" style={{display:'flex' ,width:'50%', margin:5}}>
                    <Typography textAlign={"left"}>
                        Select Processor:
                    </Typography>
                    <Autocomplete
                        disablePortal
                        {...defaultProps}
                        renderInput={(params) => <TextField value={params.id} {...params} label="Companies" />}
                        onChange={(event,newValue) => {
                            setSelectedComp(newValue?.id),
                            setSelectedCompany(newValue),
                            setSelectedCompany(newValue?.id)
                        }}
                    />
                    <Typography textAlign={"left"}>
                        Select Customer:
                    </Typography>
                    <Autocomplete
                        disablePortal
                        {...defaultProps}
                        renderInput={(params) => <TextField value={params.id} {...params} label="Companies" />}
                        onChange={(event,newValue) => {
                            setSelectedCust(newValue?.id),
                            setSelectedCustomer(newValue),
                            setSelectedCustomer(newValue?.id)
                        }}
                    />
                </FormControl>
                <div className="selectorChild" style={{display:'flex', flexDirection:'column', width:'50%'}}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <Typography textAlign={"left"}>
                            Filter Company List:
                        </Typography>
                        <Box style={{width:'100%', margin:5, display:'flex', flexDirection:'row'}}>
                            <DatePicker
                                className='DatePicker'
                                views={['year']}
                                label="Year"
                                value={endFilterListDate}
                                onChange={ (selectedValue) => {
                                    const filterStart = dayjs(selectedValue).startOf('year').format();
                                    const filterEnd = dayjs(selectedValue).endOf('year').format();
                                    setStartFilterListDate(dayjs(selectedValue).startOf('year'));
                                    setEndFilterListDate(dayjs(selectedValue).endOf('year'));
                                    setListFilterDates({ startDate: filterStart, endDate: filterEnd });
                                }}
                                renderInput={ (params) => <TextField {...params} />}
                            />
                        </Box>
                    </LocalizationProvider>
                    <Box style={{width:'100%', margin:5, display:'flex', flexDirection:'row'}}>
                        <Button variant='outlined' color='warning' style={{height:'75%', alignSelf:'center', margin:'5px', fontSize:'small'}} onClick={()=>{resetCompany()}}>Reset Company List</Button>
                    </Box>
                </div>
                </div>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <div className="selectorSubDiv" style={{display:'flex', flexDirection:'column', border:'solid darkgrey 2px', padding:'2px', width:'50%'}}>
                        <Typography textAlign={"left"}>
                            Set Invoice Date Range:
                        </Typography>
                        <Box className="dateSubDiv" style={{width:'100%', margin:5, display:'flex', flexDirection:'row'}}>
                            <div style={{marginRight:'5px'}}>
                            <DatePicker
                                views={['year', 'month', 'day']}
                                className='DatePicker'
                                label="Start Date"
                                value={startDate}
                                onChange={ (selectedValue) => {
                                    setStartDate(dayjs(selectedValue).startOf('day'));
                                    setExportStartDate(dayjs(selectedValue).startOf('day').format());
                                }}
                                renderInput={ (params) => <TextField {...params} />}
                            />
                            </div>
                            <div>
                            <DatePicker
                                className='DatePicker'
                                label="End Date"
                                value={endDate}
                                onChange={ (selectedValue) => {
                                    setEndDate(dayjs(selectedValue).endOf('day'));
                                    setExportEndDate(dayjs(selectedValue).endOf('day').format());
                                }}
                                renderInput={ (params) => <TextField {...params} />}
                            />
                            </div>
                        </Box>
                        <Box className="dateSubDiv" style={{width:'100%', margin:5, display:'flex', flexDirection:'row'}}>
                            <Button variant='contained' color="success" style={{height:'75%', alignSelf:'center', margin:'5px', fontSize:'small'}} onClick={applyInvoiceDateFilter}>Fetch Invoices</Button>
                            <Button variant='outlined' color='warning' style={{height:'75%', alignSelf:'center', margin:'5px', fontSize:'small'}} onClick={()=>{resetDate()}}>Reset Invoice Selection</Button>
                            <Button variant='contained' color='secondary' style={{height:'75%', alignSelf:'center', margin:'5px', fontSize:'small'}} onClick={()=>{exportToCsv()}}>Export Invoice Data</Button>
                        </Box>
                    </div>
                </LocalizationProvider>
            </Box>
            <DataGrid
                columns={columns}
                rows={rows}
                loading={isLoading}
                pageSizeOptions={[10, 25, 50, 100]}
                rowCount={rowCount}
                paginationMode="server"
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                style={{textAlign:'left', height:'60vh'}}
            />
            <Modal
                className="invoiceModal"
                open={invoiceModal}
                onClose={handleInvoiceClose}
                hideBackdrop={true}
            >
                <div>
                    <Button onClick={() => setInvoiceModal(false)}>Close</Button>
                    <SentBotmarkEstimateViewer className="estimateDiv" item={invoiceModelObj} handleInvoiceClose={handleInvoiceClose}/>
                </div>
            </Modal>
        </Box>
        </Page>
    )
}

export default BotmarkEstimateTable;